import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useHistory, useParams } from 'react-router-dom';
import { ToastType } from '@alefeducation/ui-elements-library-core';
import { ApolloError } from '@apollo/client/errors';
import { isEmpty } from 'lodash';
import { Member } from '../../apollo/queries';
import { injectAutoHideToastOfType } from '../../utils/Toast';
import { is1dArrayEqual } from '../../utils/1dArrayEquality';
import { GET_USER_BY_ID_V3 } from '../../apollo/queries/v3/getUserByIdV3.queries';
import {
  UPDATE_USER_V3,
  UPDATE_USER_V3_FOR_ORGANISATION,
  UpdateUserInputV3,
  UpdateUserInputV3ForOrg,
  UpdateUserV3ForOrgResponse,
  UpdateUserV3Response,
} from '../../apollo/queries/v3/updateUserV3.queries';
import { GET_USERS_V3 } from '../../apollo/queries/v3/getUsersV3.queries';
import useMemberForm from '../hooks/useMemberForm';
import { RoleSelection } from '../../components/Roles/roles.types';
import { MembershipInputV3 } from '../../apollo/queries/v3/createUserV3.queries';
import useAddEditUserRole from '../hooks/useAddEditUserRole';
import { isPasswordStrong } from '../../utils/Validators';
import {
  MEMBERSHIP_SUCCESSFULLY_UPDATED,
  SOMETHING_WENT_WRONG_MESSAGE,
} from '../../utils/constants';
import { FEATURE_TOGGLE } from '../../utils/FeatureToggle';
import appLocalStorage from '../../utils/AppLocalStorage';
import useAdminByEmailAPI from '../AddAdminUser/hooks/useAdminByEmailAPI';

interface LocationState {
  pageNumber: number;
}

interface Props {
  isOrgMember: boolean;
}

export const useEditMember = ({ isOrgMember = false }: Props) => {
  const history = useHistory();
  const pageNumber = (history.location.state as LocationState).pageNumber;
  const { code: organisationCode } = useParams<{ code: string }>();
  const { userId } = useParams<{ userId: string }>();
  const {
    name,
    firstName,
    lastName,
    password,
    confirmPassword,
    email,
    setName,
    setEmail,
    setFirstName,
    setLastName,
    setPassword,
    setConfirmPassword,
  } = useMemberForm();
  const {
    selectedRoles,
    setSelectedRoles,
    setSelectedOrgs,
    getMembershipInputV3,
  } = useAddEditUserRole();
  const [showSuccessToast, setShowSuccessToast] = useState<boolean>(false);
  const [isEmailChanged, setIsEmailChanged] = useState(false);
  const [userData, setUserData] = useState<Member | null>(null);
  const { data, loading } = useQuery(GET_USER_BY_ID_V3, {
    variables: { id: userId },
  });

  useEffect(() => {
    if (data?.userByIdV3) {
      const user = data?.userByIdV3;
      onCompleteV2(user);
    }
  }, [data]);

  const setBasicDetails = (user: Member) => {
    setUserData(user);
    setName(user.name || '');
    setFirstName(user.firstName || '');
    setLastName(user.lastName || '');
    setEmail(user.email);
  };

  const getMembershipV2Roles = (user: Member) => {
    const roles: RoleSelection[] = [];
    user.membershipsV2.forEach((mem) => {
      roles.push(
        ...mem.rolesWithName.map((role) => ({
          id: role.id,
          name: role.name,
          org: mem.organisation,
        })),
      );
    });

    return roles;
  };

  const onCompleteV2 = (user: Member) => {
    setBasicDetails(user);

    const roles = getMembershipV2Roles(user);
    setSelectedRoles(roles);
  };

  const updateMemberError = (e: ApolloError) => {
    injectAutoHideToastOfType(
      'edit-member-toast',
      SOMETHING_WENT_WRONG_MESSAGE,
      ToastType.ERROR,
    );
  };

  const getRefetchQueriesForUsers = () => [
    {
      query: GET_USERS_V3,
      variables: {
        organisation: organisationCode,
        pageNumber,
      },
    },
  ];

  const redirectToOrgMembers = () =>
    history.push(`/organisations/${organisationCode}/members`, {
      pageNumber,
    });

  const redirectToMembersList = () =>
    history.push(`/members`, {
      pageNumber,
    });

  const showUpdateToast = () => {
    injectAutoHideToastOfType(
      'edit-status-toast',
      MEMBERSHIP_SUCCESSFULLY_UPDATED,
      ToastType.SUCCESS,
    );
  };

  const redirectToMembersPage = () => {
    if (isOrgMember) {
      redirectToOrgMembers();
    } else {
      redirectToMembersList();
    }
  };

  const [updateUserV3] = useMutation(UPDATE_USER_V3, {
    onCompleted: (data: UpdateUserV3Response) => {
      if (data.updateUserV3) {
        redirectToMembersPage();
        showUpdateToast();
      }
    },
    refetchQueries: getRefetchQueriesForUsers(),
    onError: updateMemberError,
  });

  const [updateUserV3ForOrg] = useMutation(UPDATE_USER_V3_FOR_ORGANISATION, {
    onCompleted: (data: UpdateUserV3ForOrgResponse) => {
      if (data.updateUserV3ForOrganisation) {
        redirectToMembersPage();
        showUpdateToast();
      }
    },
    refetchQueries: getRefetchQueriesForUsers(),
    onError: updateMemberError,
  });

  const onUpdateMemberV3 = async () => {
    const memberships: MembershipInputV3[] = getMembershipInputV3();
    const input: UpdateUserInputV3 = {
      id: userData!.id,
      name,
      email,
      firstName,
      lastName,
      memberships,
    };

    if (password !== '') {
      input.password = password;
    }

    if (isOrgMember) {
      const { email, memberships, ...rest } = input;
      const membership = memberships.find(
        (membership) => membership.organisation === organisationCode,
      );
      const inputOrg: UpdateUserInputV3ForOrg = {
        ...rest,
        membership: membership!,
      };

      await updateUserV3ForOrg({ variables: { input: inputOrg } });
    } else {
      await updateUserV3({ variables: { input } });
    }
  };

  const isPassMissMatch = () => password.trim() !== confirmPassword.trim();
  const isWeakPassword = () =>
    password.trim().length > 0 ? !isPasswordStrong(password) : false;

  const createStringArray = (roleSelections: RoleSelection[]) =>
    roleSelections.map((role) => `${role.id}-${role.org}-${role.name}`);

  const isFormInValid = (): boolean => {
    if (userData === null) {
      return true;
    }

    if (selectedRoles.length === 0 || isPassMissMatch() || isWeakPassword()) {
      return true;
    }

    const roles: RoleSelection[] = getMembershipV2Roles(userData);

    const areRolesEqual = is1dArrayEqual(
      createStringArray(selectedRoles),
      createStringArray(roles),
    );

    if (FEATURE_TOGGLE.ENABLE_AUTH0_INTEGRATION) {
      const fName = userData.firstName || '';
      const lName = userData.lastName || '';
      const eMail = userData.email || '';

      if (isEmpty(email) || isEmpty(firstName) || isEmpty(lastName)) {
        return true;
      }

      if (
        areRolesEqual &&
        name.trim() === userData.name &&
        firstName.trim() === fName &&
        lastName.trim() === lName &&
        email.trim() === eMail
      ) {
        if (password.trim().length && confirmPassword.trim().length) {
          return password.trim() !== confirmPassword.trim() || isWeakPassword();
        }
        return true;
      }
    } else {
      if (isEmpty(name)) {
        return true;
      }

      if (areRolesEqual && name.trim() === userData.name) {
        if (password.trim().length && confirmPassword.trim().length) {
          return password.trim() !== confirmPassword.trim() || isWeakPassword();
        }
        return true;
      }
    }
    return false;
  };

  const { adminByEmail, message } = useAdminByEmailAPI({
    onComplete: () => {
      setIsEmailChanged(false);
    },
  });

  const onCheckEmail = () => {
    adminByEmail(email);
  };

  const form = {
    selectedRoles,
    password,
    confirmPassword,
    name,
    firstName,
    lastName,
    email,
    setSelectedOrgs,
    isFormInValid,
    isEmailChanged,
    setIsEmailChanged,
    messageText: message,
    onRoleChange: setSelectedRoles,
    onNameChange: setName,
    onEmailChange: setEmail,
    onCheckEmail: onCheckEmail,
    onFirstNameChange: setFirstName,
    onLastNameChange: setLastName,
    onPassChange: setPassword,
    onConfirmPassChange: setConfirmPassword,
    onCancelClick: isOrgMember ? redirectToOrgMembers : redirectToMembersList,
    onEditMember: onUpdateMemberV3,
  };

  const toast = { showSuccessToast, setShowSuccessToast };
  const userQuery = {
    loading,
  };

  return {
    form,
    isViewOnlyRole: appLocalStorage.isViewOnlyRole(),
    toast,
    userQuery,
    userData,
  };
};
